import React, {forwardRef} from 'react';
import {Diff} from 'utility-types';

import {Renderer} from '../../../../core/src/helpers/react/reactHelpers';
import {VisualSizesEnum} from '../../styles/commonStyles';
import {TooltipCoordinator, TooltipCoordinatorProps} from '../tooltips/tooltipCoordinator';
import {ButtonChevron, ButtonChevronProps} from './buttonChevron';
import {ButtonChrome, ButtonInheritableProps, defaultButtonProps} from './buttonChrome';
import {ButtonContent, ButtonContentProps} from './buttonContent';
import {ChromelessButton, ChromelessButtonProps} from './chromelessButton';

/*
 * Props.
 */

export interface ButtonProps
  extends ButtonInheritableProps,
    ButtonContentProps,
    ChromelessButtonProps,
    ButtonChevronProps {
  tooltipProps?: Partial<TooltipCoordinatorProps>;
  renderTooltip?: Renderer;
  isDropdownDisabled?: boolean;
}

export type DefaultedButtonProps = Diff<ButtonProps, typeof defaultButtonProps> &
  Partial<typeof defaultButtonProps>;

/*
 * Component.
 */

/**
 * The standard text button for triggering actions, with configurable size, variant, and color, plus
 * optional leading/trailing icons and a dropdown chevron. Use it for any clickable action that carries
 * a text label. For icon-only actions in space-constrained places, use IconButton instead.
 *
 * @summary for text-labelled actions such as submitting, confirming, or opening a related flow
 */
export const Button = forwardRef<HTMLDivElement, DefaultedButtonProps>(
  ({className, children, size = 'medium', variant = 'secondary', color = 'default', ...buttonProps}, ref) => (
    <ButtonChrome
      ref={ref}
      className={className}
      {...buttonProps}
      size={size}
      variant={variant}
      color={color}
    >
      {() => (
        <>
          <TooltipCoordinator render={buttonProps.renderTooltip} {...buttonProps.tooltipProps}>
            <ChromelessButton {...buttonProps}>
              <ButtonContent {...buttonProps} size={size} variant={variant} color={color}>
                {children}
              </ButtonContent>
            </ChromelessButton>
          </TooltipCoordinator>
          {maybeRenderChevron({...buttonProps, size, variant, color})}
        </>
      )}
    </ButtonChrome>
  ),
);

function maybeRenderChevron(props: ButtonProps) {
  if (!props.renderDropdown || props.size === VisualSizesEnum.LARGE) {
    return null;
  }

  return <ButtonChevron {...props} isDisabled={props.isDropdownDisabled} />;
}
